import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ArrowUpRight, ArrowLeft, CheckCircle, Clock, BookOpen, Target, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Enrollment = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Assessment state
  const [assessment, setAssessment] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  const [formData, setFormData] = useState({
    student_first_name: '',
    student_last_name: '',
    student_email: '',
    student_age: '',
    student_grade: '',
    parent_first_name: '',
    parent_last_name: '',
    parent_email: '',
    parent_phone: '',
    program: '',
    experience_level: '',
    how_heard: '',
    additional_info: ''
  });

  // Fetch assessment when entering step 4
  useEffect(() => {
    if (step === 4 && !assessment && !assessmentResult) {
      fetchAssessment();
    }
  }, [step, assessment, assessmentResult]);

  const handleAssessmentSubmit = useCallback(async () => {
    if (assessmentSubmitting) return;
    
    setAssessmentSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, selected_option]) => ({
        question_id,
        selected_option
      }));

      const res = await axios.post(`${API}/placement-assessment/submit`, {
        answers: formattedAnswers
      });
      
      setAssessmentResult(res.data);
      toast.success('Assessment completed!');
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      toast.error('Failed to submit assessment. Please try again.');
    } finally {
      setAssessmentSubmitting(false);
    }
  }, [answers, assessmentSubmitting]);

  // Timer for assessment
  useEffect(() => {
    if (step === 4 && assessment && timeRemaining > 0 && !assessmentResult) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAssessmentSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, assessment, timeRemaining, assessmentResult, handleAssessmentSubmit]);

  const fetchAssessment = async () => {
    setAssessmentLoading(true);
    try {
      const res = await axios.get(`${API}/placement-assessment`);
      setAssessment(res.data);
      setTimeRemaining(res.data.time_limit_minutes * 60);
    } catch (error) {
      console.error('Failed to fetch assessment:', error);
      toast.error('Failed to load assessment. Please try again.');
    } finally {
      setAssessmentLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.student_first_name || !formData.student_last_name || !formData.student_email || !formData.student_age || !formData.student_grade) {
      toast.error('Please fill in all student information');
      return false;
    }
    if (parseInt(formData.student_age) < 10 || parseInt(formData.student_age) > 25) {
      toast.error('Student age must be between 10 and 25');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.parent_first_name || !formData.parent_last_name || !formData.parent_email || !formData.parent_phone) {
      toast.error('Please fill in all parent/guardian information');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.program || !formData.experience_level || !formData.how_heard) {
      toast.error('Please fill in all program information');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionIndex }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < assessment.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      const payload = {
        ...formData,
        student_age: parseInt(formData.student_age),
        // Include assessment results if available
        assessment_score: assessmentResult?.score || null,
        assessment_percentage: assessmentResult?.percentage || null,
        recommended_level: assessmentResult?.recommended_level || null
      };
      await axios.post(`${API}/enrollment`, payload);
      setSubmitted(true);
      toast.success('Enrollment submitted successfully!');
    } catch (error) {
      console.error('Enrollment error:', error);
      toast.error('Failed to submit enrollment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'text-[#00FFE0]';
      case 'intermediate': return 'text-[#00AEEF]';
      case 'advanced': return 'text-[#FF6A00]';
      default: return 'text-white';
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-[#00FFE0]/20 border-[#00FFE0]';
      case 'intermediate': return 'bg-[#00AEEF]/20 border-[#00AEEF]';
      case 'advanced': return 'bg-[#FF6A00]/20 border-[#FF6A00]';
      default: return 'bg-white/20 border-white';
    }
  };

  const inputClass = "bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all";
  const labelClass = "block text-sm font-medium text-[#94A3B8] mb-2";
  const selectClass = "bg-[#060F22] border border-[#00AEEF]/30 text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all appearance-none cursor-pointer";

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#00FFE0]" />
          </div>
          <h1 className="text-4xl font-black text-white mb-4">Application Received!</h1>
          <p className="text-lg text-[#94A3B8] mb-4">
            Thank you for enrolling in NextGen Tech Pathways. We've sent a confirmation email to {formData.parent_email}. Our team will review your application and contact you within 2-3 business days.
          </p>
          {assessmentResult && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getLevelBadgeColor(assessmentResult.recommended_level)} mb-6`}>
              <Target className="w-4 h-4" />
              <span className={`font-bold capitalize ${getLevelColor(assessmentResult.recommended_level)}`}>
                Recommended Level: {assessmentResult.recommended_level}
              </span>
            </div>
          )}
          <div>
            <a
              href="/"
              data-testid="enrollment-success-home-btn"
              className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all duration-300 gap-2"
            >
              Return Home
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0A1F44] via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
            Join Our Program
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-tight mb-4">
            Start Your Journey Today
          </h1>
          <p className="text-lg text-[#94A3B8]">
            Complete the enrollment form below to apply for our next cohort.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 sm:py-24 bg-[#0A1F44]/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-12">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                  step >= s 
                    ? 'bg-[#00FFE0] text-[#060F22]' 
                    : 'bg-[#0A1F44] text-[#94A3B8] border border-[#00AEEF]/30'
                }`}>
                  {s}
                </div>
                {s < 4 && (
                  <div className={`w-12 sm:w-20 h-1 mx-2 rounded ${
                    step > s ? 'bg-[#00FFE0]' : 'bg-[#0A1F44]'
                  }`}></div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 sm:p-10">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Student Information */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2">Student Information</h2>
                    <p className="text-[#94A3B8]">Tell us about the student who will be joining the program.</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>First Name *</label>
                      <input
                        type="text"
                        name="student_first_name"
                        value={formData.student_first_name}
                        onChange={handleChange}
                        data-testid="enrollment-student-first-name"
                        className={inputClass}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name *</label>
                      <input
                        type="text"
                        name="student_last_name"
                        value={formData.student_last_name}
                        onChange={handleChange}
                        data-testid="enrollment-student-last-name"
                        className={inputClass}
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Student Email *</label>
                    <input
                      type="email"
                      name="student_email"
                      value={formData.student_email}
                      onChange={handleChange}
                      data-testid="enrollment-student-email"
                      className={inputClass}
                      placeholder="student@email.com"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Age *</label>
                      <input
                        type="number"
                        name="student_age"
                        value={formData.student_age}
                        onChange={handleChange}
                        data-testid="enrollment-student-age"
                        className={inputClass}
                        placeholder="Enter age"
                        min="10"
                        max="25"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Current Grade *</label>
                      <select
                        name="student_grade"
                        value={formData.student_grade}
                        onChange={handleChange}
                        data-testid="enrollment-student-grade"
                        className={selectClass}
                      >
                        <option value="">Select grade</option>
                        <option value="6th Grade">6th Grade</option>
                        <option value="7th Grade">7th Grade</option>
                        <option value="8th Grade">8th Grade</option>
                        <option value="9th Grade">9th Grade (Freshman)</option>
                        <option value="10th Grade">10th Grade (Sophomore)</option>
                        <option value="11th Grade">11th Grade (Junior)</option>
                        <option value="12th Grade">12th Grade (Senior)</option>
                        <option value="College Freshman">College Freshman</option>
                        <option value="College Sophomore">College Sophomore</option>
                        <option value="College Junior">College Junior</option>
                        <option value="College Senior">College Senior</option>
                        <option value="Not in School">Not Currently in School</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Parent/Guardian Information */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2">Parent/Guardian Information</h2>
                    <p className="text-[#94A3B8]">We'll use this information to contact you about the enrollment.</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>First Name *</label>
                      <input
                        type="text"
                        name="parent_first_name"
                        value={formData.parent_first_name}
                        onChange={handleChange}
                        data-testid="enrollment-parent-first-name"
                        className={inputClass}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name *</label>
                      <input
                        type="text"
                        name="parent_last_name"
                        value={formData.parent_last_name}
                        onChange={handleChange}
                        data-testid="enrollment-parent-last-name"
                        className={inputClass}
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Email Address *</label>
                    <input
                      type="email"
                      name="parent_email"
                      value={formData.parent_email}
                      onChange={handleChange}
                      data-testid="enrollment-parent-email"
                      className={inputClass}
                      placeholder="parent@email.com"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Phone Number *</label>
                    <input
                      type="tel"
                      name="parent_phone"
                      value={formData.parent_phone}
                      onChange={handleChange}
                      data-testid="enrollment-parent-phone"
                      className={inputClass}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Program Selection */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2">Program Selection</h2>
                    <p className="text-[#94A3B8]">Choose the program that best fits your goals.</p>
                  </div>

                  <div>
                    <label className={labelClass}>Select Program *</label>
                    <select
                      name="program"
                      value={formData.program}
                      onChange={handleChange}
                      data-testid="enrollment-program"
                      className={selectClass}
                    >
                      <option value="">Select a program</option>
                      <option value="Web Development Foundations">Web Development Foundations (8 weeks)</option>
                      <option value="App Development Track">App Development Track (8 weeks)</option>
                      <option value="Cloud & DevOps Intro">Cloud & DevOps Intro (6 weeks)</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Current Experience Level *</label>
                    <select
                      name="experience_level"
                      value={formData.experience_level}
                      onChange={handleChange}
                      data-testid="enrollment-experience"
                      className={selectClass}
                    >
                      <option value="">Select experience level</option>
                      <option value="No Experience">No Experience - Complete Beginner</option>
                      <option value="Some Basics">Some Basics - Tried coding before</option>
                      <option value="Intermediate">Intermediate - Built small projects</option>
                      <option value="Advanced">Advanced - Looking to specialize</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>How did you hear about us? *</label>
                    <select
                      name="how_heard"
                      value={formData.how_heard}
                      onChange={handleChange}
                      data-testid="enrollment-how-heard"
                      className={selectClass}
                    >
                      <option value="">Select an option</option>
                      <option value="Social Media">Social Media</option>
                      <option value="School/Teacher">School/Teacher</option>
                      <option value="Friend/Family">Friend/Family</option>
                      <option value="Community Organization">Community Organization</option>
                      <option value="Online Search">Online Search</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Additional Information (Optional)</label>
                    <textarea
                      name="additional_info"
                      value={formData.additional_info}
                      onChange={handleChange}
                      data-testid="enrollment-additional-info"
                      className={`${inputClass} min-h-[120px] resize-none`}
                      placeholder="Tell us anything else you'd like us to know..."
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Placement Assessment */}
              {step === 4 && (
                <div className="space-y-6">
                  {assessmentLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-12 h-12 text-[#00AEEF] animate-spin mb-4" />
                      <p className="text-[#94A3B8]">Loading assessment...</p>
                    </div>
                  ) : assessmentResult ? (
                    /* Assessment Results */
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <div className="w-20 h-20 rounded-full bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-4">
                          <Target className="w-10 h-10 text-[#00FFE0]" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Assessment Complete!</h2>
                        <p className="text-[#94A3B8]">Here's your personalized recommendation.</p>
                      </div>

                      {/* Score Summary */}
                      <div className="bg-[#060F22] rounded-xl p-6 border border-[#00AEEF]/30">
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="text-center">
                            <p className="text-4xl font-black text-[#00FFE0]">{assessmentResult.score}</p>
                            <p className="text-sm text-[#94A3B8]">Points Earned</p>
                          </div>
                          <div className="text-center">
                            <p className="text-4xl font-black text-white">{assessmentResult.percentage}%</p>
                            <p className="text-sm text-[#94A3B8]">Score</p>
                          </div>
                        </div>

                        <div className="border-t border-[#00AEEF]/20 pt-6">
                          <p className="text-sm text-[#94A3B8] mb-2">Recommended Starting Level</p>
                          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getLevelBadgeColor(assessmentResult.recommended_level)}`}>
                            <span className={`text-lg font-bold capitalize ${getLevelColor(assessmentResult.recommended_level)}`}>
                              {assessmentResult.recommended_level}
                            </span>
                          </div>
                          <p className="text-sm text-[#94A3B8] mt-3">{assessmentResult.level_description}</p>
                        </div>
                      </div>

                      {/* Category Breakdown */}
                      <div className="bg-[#060F22] rounded-xl p-6 border border-[#00AEEF]/30">
                        <h3 className="text-lg font-bold text-white mb-4">Skills Breakdown</h3>
                        <div className="space-y-3">
                          {Object.entries(assessmentResult.category_breakdown || {}).map(([category, scores]) => (
                            <div key={category} className="flex items-center justify-between">
                              <span className="text-[#94A3B8] capitalize">{category.replace('_', ' ')}</span>
                              <span className="text-white font-medium">
                                {scores.correct}/{scores.total} correct
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <p className="text-center text-[#94A3B8] text-sm">
                        Click "Submit Application" below to complete your enrollment with this assessment result.
                      </p>
                    </div>
                  ) : assessment ? (
                    /* Assessment Quiz */
                    <div className="space-y-6">
                      <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-2xl font-bold text-white">{assessment.title}</h2>
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                            timeRemaining <= 60 ? 'bg-red-500/20 text-red-400' : 'bg-[#00AEEF]/20 text-[#00AEEF]'
                          }`}>
                            <Clock className="w-4 h-4" />
                            <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
                          </div>
                        </div>
                        <p className="text-[#94A3B8]">{assessment.description}</p>
                      </div>

                      {/* Question Progress */}
                      <div className="flex items-center gap-2 mb-6 flex-wrap">
                        {assessment.questions.map((q, idx) => (
                          <button
                            key={q.id}
                            type="button"
                            onClick={() => setCurrentQuestionIndex(idx)}
                            data-testid={`assessment-question-nav-${idx}`}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                              currentQuestionIndex === idx
                                ? 'bg-[#FF6A00] text-white'
                                : answers[q.id] !== undefined
                                ? 'bg-[#00FFE0] text-[#060F22]'
                                : 'bg-[#0A1F44] text-[#94A3B8] border border-[#00AEEF]/30'
                            }`}
                          >
                            {idx + 1}
                          </button>
                        ))}
                      </div>

                      {/* Current Question */}
                      <div className="bg-[#060F22] rounded-xl p-6 border border-[#00AEEF]/30">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-xs px-2 py-1 rounded bg-[#00AEEF]/20 text-[#00AEEF] capitalize">
                            {assessment.questions[currentQuestionIndex].category.replace('_', ' ')}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-[#FF6A00]/20 text-[#FF6A00]">
                            {assessment.questions[currentQuestionIndex].points} pts
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-bold text-white mb-6">
                          {currentQuestionIndex + 1}. {assessment.questions[currentQuestionIndex].question}
                        </h3>

                        <div className="space-y-3">
                          {assessment.questions[currentQuestionIndex].options.map((option, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleAnswerSelect(assessment.questions[currentQuestionIndex].id, idx)}
                              data-testid={`assessment-option-${idx}`}
                              className={`w-full text-left p-4 rounded-xl border transition-all ${
                                answers[assessment.questions[currentQuestionIndex].id] === idx
                                  ? 'bg-[#FF6A00]/20 border-[#FF6A00] text-white'
                                  : 'bg-[#0A1F44]/50 border-[#00AEEF]/20 text-[#94A3B8] hover:border-[#00AEEF]/50 hover:text-white'
                              }`}
                            >
                              <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}.</span>
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Question Navigation */}
                      <div className="flex justify-between items-center">
                        <button
                          type="button"
                          onClick={handlePrevQuestion}
                          disabled={currentQuestionIndex === 0}
                          data-testid="assessment-prev-btn"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A1F44] text-[#94A3B8] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Previous
                        </button>

                        <span className="text-[#94A3B8] text-sm">
                          {Object.keys(answers).length} of {assessment.questions.length} answered
                        </span>

                        {currentQuestionIndex === assessment.questions.length - 1 ? (
                          <button
                            type="button"
                            onClick={handleAssessmentSubmit}
                            disabled={assessmentSubmitting || Object.keys(answers).length === 0}
                            data-testid="assessment-submit-btn"
                            className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-[#00FFE0] text-[#060F22] font-bold hover:bg-[#00FFE0]/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            {assessmentSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                Submit Assessment
                                <CheckCircle className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleNextQuestion}
                            data-testid="assessment-next-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6A00] text-white font-bold hover:bg-[#FF6A00]/80 transition-all"
                          >
                            Next
                            <ArrowUpRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BookOpen className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                      <p className="text-[#94A3B8]">Failed to load assessment. Please go back and try again.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-10">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    data-testid="enrollment-back-btn"
                    className="inline-flex items-center justify-center bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 hover:text-white font-bold rounded-full px-6 py-3 transition-all duration-300 gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                ) : (
                  <div></div>
                )}

                {step < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    data-testid="enrollment-next-btn"
                    className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2"
                  >
                    Continue
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                ) : assessmentResult ? (
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="enrollment-submit-btn"
                    className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                    ) : (
                      <>
                        Submit Application
                        <ArrowUpRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enrollment;
